/**
 * A generic, field-by-field structural diff between two JSON-like objects — the mechanism
 * behind the version diff view in docs/PLAN.md §6. Deliberately shape-agnostic (works for both
 * PrdContent and TddContent) rather than schema-specific, so adding a field to either document
 * type doesn't require touching this file.
 */
export type FieldDiffStatus = 'unchanged' | 'added' | 'removed' | 'changed';

export interface FieldDiff {
  path: string;
  status: FieldDiffStatus;
  before: unknown;
  after: unknown;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** True for an array of plain objects that each have a stable string `id` — e.g. TddContent's
 * `sections` or PrdContent's `userStories`. Vacuously true for an empty array, since there's
 * nothing to contradict "every item has an id". */
function isIdKeyedArray(v: unknown): v is Array<Record<string, unknown> & { id: string }> {
  return Array.isArray(v) && v.every((item) => isPlainObject(item) && typeof item.id === 'string');
}

function stableStringify(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
  if (isPlainObject(v)) {
    return `{${Object.keys(v)
      .sort()
      .map((k) => `${k}:${stableStringify(v[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(v);
}

/** Diffs two values at `path`, pushing one FieldDiff per leaf-ish field into `out`. Objects
 * recurse field by field. Arrays of id-keyed objects (sections, user stories, ...) are matched
 * by id and recursed into as well, so editing one section's body reports just that section's
 * changed field instead of the entire array as one opaque "changed" blob — that used to be the
 * whole-array behavior below, which is still right for a plain array like `outOfScope` with no
 * per-item identity to match on ("3rd item in a reordered list" rarely means "the same
 * conceptual field"). */
function diffValue(path: string, before: unknown, after: unknown, out: FieldDiff[]): void {
  if (isPlainObject(before) && isPlainObject(after)) {
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    for (const key of keys) {
      diffValue(path ? `${path}.${key}` : key, before[key], after[key], out);
    }
    return;
  }

  if (isIdKeyedArray(before) && isIdKeyedArray(after) && (before.length > 0 || after.length > 0)) {
    const beforeById = new Map(before.map((item) => [item.id, item]));
    const afterById = new Map(after.map((item) => [item.id, item]));
    const ids = new Set([...beforeById.keys(), ...afterById.keys()]);
    for (const id of ids) {
      diffValue(`${path}[${id}]`, beforeById.get(id), afterById.get(id), out);
    }
    return;
  }

  const beforeStr = stableStringify(before ?? null);
  const afterStr = stableStringify(after ?? null);
  if (beforeStr === afterStr) {
    out.push({ path, status: 'unchanged', before, after });
    return;
  }
  const status: FieldDiffStatus =
    before === undefined ? 'added' : after === undefined ? 'removed' : 'changed';
  out.push({ path, status, before, after });
}

export function diffObjects(before: unknown, after: unknown): FieldDiff[] {
  const out: FieldDiff[] = [];
  diffValue('', before, after, out);
  return out.filter((d) => d.path !== '');
}
